import type { DnsRecordDto } from "@bafe/api-client";
import { Card, CopyField, StatusPill } from "@bafe/ui";

import { formatRelative } from "../lib/format";
import { DNS_RECORD_STATUS } from "../lib/status";

export interface DnsRecordListProps {
	records: DnsRecordDto[];
	/** Shows the "why this record exists" sentence under each row. */
	showExplanations?: boolean;
}

export function DnsRecordList({ records, showExplanations = true }: DnsRecordListProps) {
	return (
		<ul className="flex flex-col gap-3">
			{records.map((record) => {
				const status = DNS_RECORD_STATUS[record.status];
				return (
					<li key={record.id}>
						<Card className="flex flex-col gap-3">
							<div className="flex flex-wrap items-start justify-between gap-2">
								<div className="flex flex-col gap-0.5">
									<h4 className="text-sm font-semibold text-(--text-primary)">
										{record.recordType} record
										{record.required ? "" : " (optional)"}
									</h4>
									<p className="text-sm text-(--text-secondary)">{record.message}</p>
								</div>
								<StatusPill tone={status.tone}>{status.label}</StatusPill>
							</div>

							{showExplanations && (
								<p className="max-w-prose text-sm text-(--text-secondary)">{record.explanation}</p>
							)}

							<dl className="grid gap-3 sm:grid-cols-2">
								<div className="flex flex-col gap-1">
									<dt className="text-xs font-medium text-(--text-muted)">Type</dt>
									<dd className="text-dns text-(--text-primary)">{record.recordType}</dd>
								</div>
								<div className="flex flex-col gap-1">
									<dt className="text-xs font-medium text-(--text-muted)">Host or name</dt>
									<dd>
										<CopyField value={record.host} label={`${record.recordType} host`} />
									</dd>
								</div>
								{record.priority !== null && (
									<div className="flex flex-col gap-1">
										<dt className="text-xs font-medium text-(--text-muted)">Priority</dt>
										<dd>
											<CopyField value={String(record.priority)} label="Priority" />
										</dd>
									</div>
								)}
								<div className="flex flex-col gap-1 sm:col-span-2">
									<dt className="text-xs font-medium text-(--text-muted)">Value</dt>
									<dd>
										<CopyField value={record.value} label={`${record.recordType} value`} />
									</dd>
								</div>
							</dl>

							{record.observedValues.length > 0 && record.status !== "detected" && (
								<details>
									<summary className="cursor-pointer text-xs text-(--text-muted)">
										What we found instead
									</summary>
									<ul className="mt-2 flex flex-col gap-1">
										{record.observedValues.map((value) => (
											<li key={value} className="text-dns text-(--text-secondary)">
												{value}
											</li>
										))}
									</ul>
								</details>
							)}

							<p className="text-xs text-(--text-muted)">
								Last checked {formatRelative(record.lastCheckedAt)}
							</p>
						</Card>
					</li>
				);
			})}
		</ul>
	);
}
